/**
 * useAmenityCatalog — TanStack Query hook over
 * `GET /organizations/:slug/properties/amenities/catalog` (SCRUM-141 /
 * DS-010-5).
 *
 * The catalog is a PLATFORM-GLOBAL list (the same amenities for every property
 * in the org), gated on `property.amenity.read` — there is NO property-access
 * guard and, per the contract (openapi.d.ts documents no 404 on this path), NO
 * not-found branch. Only 403 has a dedicated sentinel; everything else non-ok
 * / undefined-body throws → `error`. Mirrors `use-property-detail.ts`
 * (org slug from `useCurrentOrg`, `retry: false`, FORBIDDEN Symbol sentinel,
 * closed status union) minus the 404 branch.
 *
 * The query CACHE holds the RAW `AmenityCatalogListResponseDto`; the grouping
 * transform (`groupCatalog`) is applied at READ via `React.useMemo` so the
 * cached entry stays the canonical server response (and the set-mutation's
 * optimistic write can look up items off that same raw cache).
 */

import * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import type { components } from '@artemis/contracts';
import { apiClient } from '../../../shared/api-client.js';
import { useCurrentOrg } from '../../../shared/hooks/useCurrentOrg.js';
import { propertyQueryKeys } from './queryKeys.js';
import { groupCatalog, type CatalogGroup } from './amenities-mapper.js';

type AmenityCatalogListResponseDto =
  components['schemas']['AmenityCatalogListResponseDto'];

/** 403 sentinel — the caller lost `property.amenity.read` since `/auth/me` cached. */
const FORBIDDEN = Symbol('amenity-catalog:forbidden');

export type UseAmenityCatalogResult =
  | { readonly status: 'loading'; readonly data?: undefined }
  | { readonly status: 'ready'; readonly data: CatalogGroup[] }
  | { readonly status: 'forbidden'; readonly data?: undefined }
  | { readonly status: 'error'; readonly data?: undefined };

export function useAmenityCatalog(): UseAmenityCatalogResult {
  const orgState = useCurrentOrg();
  const orgSlug = orgState?.membership.organizationSlug ?? null;

  const query = useQuery({
    queryKey: [...propertyQueryKeys.amenitiesCatalog(), orgSlug] as const,
    enabled: orgSlug !== null,
    retry: false,
    queryFn: async () => {
      const result = await apiClient.GET(
        '/organizations/{slug}/properties/amenities/catalog',
        {
          params: {
            path: { slug: orgSlug as string },
          },
        },
      );
      // Global endpoint — 403 only; NO 404 branch (see file header).
      if (result.response.status === 403) {
        return FORBIDDEN;
      }
      if (!result.response.ok || result.data === undefined) {
        throw new Error(
          `GET /organizations/${orgSlug}/properties/amenities/catalog failed with status ${result.response.status}`,
        );
      }
      return result.data;
    },
  });

  // Group only when ready. Memoised on the raw DTO reference so grouping is
  // stable across re-renders that don't change the response.
  const data = React.useMemo<CatalogGroup[] | null>(() => {
    const raw = query.data;
    if (raw === undefined || raw === FORBIDDEN) {
      return null;
    }
    return groupCatalog((raw as AmenityCatalogListResponseDto).amenities);
  }, [query.data]);

  if (orgSlug === null || query.isPending) {
    return { status: 'loading' };
  }
  if (query.isError) {
    return { status: 'error' };
  }
  if (query.data === FORBIDDEN || data === null) {
    return { status: 'forbidden' };
  }

  return { status: 'ready', data };
}
